import Vue from 'vue';
import VueResource from 'vue-resource';

import { get, has, some } from 'lodash/fp';

import Auth from 'services/Auth';
import i18n from 'services/i18n';
import notifications from 'services/Notifications';
import { isLoggedIn } from 'router/helpers';
import router from 'router';
import store from '../store';

Vue.use(VueResource);

/**
 * Http wrapper
 */
const Http = {
  /**
   * Delay initialization until main.js has run
   */
  init() {
    // add auth headers if previously logged in
    if (isLoggedIn()) {
      this.setToken(Vue.localStorage.get('token'));
    }

    const locale = store.getters['account/locale'];
    this.setLocale(locale);

    // log out if unauthorised
    Vue.http.interceptors.push(function(request, next) {
      next(function(response) {
        if (
          (has('body.errors', response) &&
            some(['errorCode', 'TOKEN_EXPIRED'])(response.body.errors)) ||
          get('body.errorCode', response) === 'TOKEN_EXPIRED'
        ) {
          console.log('Token Expired!');
          Auth.logout();

          const errorText = i18n.t('login_form.errors.token_expired');

          notifications.add(errorText);

          // We do not want the actual code block to execute. So throwing error.
          throw new Error(errorText);
        }

        if (
          (has('body.errors', response) &&
            some(['name', 'UnauthorizedError'])(response.body.errors)) ||
          response.status === 401
        ) {
          console.log('Unauthorized!');
          if (
            router.currentRoute.name !== 'login' &&
            router.currentRoute.name !== 'settings'
          ) {
            Auth.logout();
          }
        }
      });
    });
  },

  /**
   * Set or remove user type
   *
   * @param   {sdk|business}  [userType]
   */
  setUserType(userType) {
    store.commit('account/userType', userType || null);

    return userType
      ? Vue.localStorage.set('userType', userType)
      : Vue.localStorage.remove('userType');
  },

  /**
   * Set or remove JWT token
   *
   * @param   {string}  [token]   A JWT token string, i.e. "e21bc75d8ea959b2237dfd499112871814097203d38e2c6a29ae014d6c12c948cdeed262e4e6cb2dae1f38f394edba74252562452de1723f531daf5fa97ff46e"
   */
  setToken(token) {
    Vue.http.headers.common['Authorization'] = token ? 'Bearer ' + token : null;
    return token
      ? Vue.localStorage.set('token', token)
      : Vue.localStorage.remove('token');
  },

  /**
   * Set refresh token
   *
   * @param {String} refreshToken
   * @returns
   */
  setRefreshToken(refreshToken) {
    return refreshToken
      ? Vue.localStorage.set('refreshToken', refreshToken)
      : Vue.localStorage.remove('refreshToken');
  },

  /**
   * Set Accept-Language header
   *
   * @param {String} locale
   */
  setLocale(locale) {
    Vue.http.headers.common['Accept-Language'] = locale;

    return Vue.localStorage.set('locale', locale);
  },

  /**
   * Send a GET request to the server
   *
   * @param   {string}    url
   * @param   {object}    params
   * @returns {Promise}
   */
  get(url, params = null) {
    return Vue.http.get(url, params);
  },

  /**
   * Send a POST request to the server
   *
   * @param   {string}    url
   * @param   {object}    params
   * @returns {Promise}
   */
  post(url, params = null) {
    return Vue.http.post(url, params);
  },

  /**
   * Send a PUT request to the server
   *
   * @param   {string}    url
   * @param   {object}    params
   * @returns {Promise}
   */
  put(url, params = null, options = {}) {
    return Vue.http.put(url, params, options);
  },

  /**
   * Send a PATCH request to the server
   *
   * @param   {string}    url
   * @param   {object}    params
   * @returns {Promise}
   */
  patch(url, params = null, options = {}) {
    return Vue.http.patch(url, params, options);
  },

  /**
   * Send a DELETE request to the server
   *
   * @param   {string}    url
   * @param   {object}    params
   * @returns {Promise}
   */
  delete(url, params = null) {
    return Vue.http.delete(url, params);
  },
};

export default Http;
